import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import L, { Map as LeafletMap, Marker as LeafletMarker } from "leaflet";
import {
  Position,
  LocationWithAddress,
  NominatimSearchResult,
  NominatimReverseResult,
} from "../types";
import {
  DEFAULT_CENTER,
  MAP_CONFIG,
  TILE_LAYER_CONFIG,
  NOMINATIM_CONFIG,
} from "../constants";
import { logErrorDev } from "../../../../utils/formatters";
import { ERROR_MESSAGES } from "../../../../utils/constants";
import { ErrorToaster } from "../../../../helpers/toasterHelper";

/**
 * Custom hook for MapLocationPicker component logic
 *
 * Manages map state, geocoding operations, and user interactions.
 * Provides methods for map initialization, location search, and coordinate selection.
 *
 * @param {Position | undefined} initialPosition - Initial position to center the map on
 * @param {(location: LocationWithAddress) => void} onSelect - Callback function for location selection
 * @returns {object} Map utilities including state, handlers, and refs
 */
export const useMapLocationPicker = (
  initialPosition: Position | undefined,
  onSelect: (location: LocationWithAddress) => void
) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const leafletMapRef = useRef<LeafletMap | null>(null);
  const markerRef = useRef<LeafletMarker | null>(null);

  const [picked, setPicked] = useState<Position | null>(
    initialPosition || null
  );
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const center = useMemo(
    () => initialPosition || DEFAULT_CENTER,
    [initialPosition]
  );

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const searchUrl = new URL(
        NOMINATIM_CONFIG.SEARCH_ENDPOINT,
        NOMINATIM_CONFIG.BASE_URL
      );
      searchUrl.searchParams.set("format", NOMINATIM_CONFIG.FORMAT);
      searchUrl.searchParams.set("q", searchQuery.trim());
      searchUrl.searchParams.set("limit", MAP_CONFIG.SEARCH_LIMIT.toString());
      searchUrl.searchParams.set(
        "addressdetails",
        NOMINATIM_CONFIG.ADDRESS_DETAILS.toString()
      );

      const response = await fetch(searchUrl.toString());

      if (response.ok) {
        const data: NominatimSearchResult[] = await response.json();
        if (data && data.length > 0) {
          const result = data[0];
          const lat = parseFloat(result.lat);
          const lng = parseFloat(result.lon);

          setPicked({ lat, lng });

          if (leafletMapRef.current) {
            leafletMapRef.current.setView([lat, lng], MAP_CONFIG.SEARCH_ZOOM);

            if (markerRef.current) {
              markerRef.current.setLatLng([lat, lng]);
            } else {
              markerRef.current = L.marker([lat, lng]).addTo(
                leafletMapRef.current
              );
            }
          }
        } else {
          ErrorToaster(ERROR_MESSAGES.MAP.LOCATION_NOT_FOUND);
        }
      } else {
        ErrorToaster(ERROR_MESSAGES.MAP.SEARCH_FAILED);
      }
    } catch (error) {
      logErrorDev(ERROR_MESSAGES.MAP.SEARCH_FAILED, error);
      ErrorToaster(ERROR_MESSAGES.MAP.SEARCH_FAILED);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch]
  );

  useEffect(() => {
    if (!mapRef.current) return;

    const map = L.map(mapRef.current, {
      center: [center.lat, center.lng],
      zoom: MAP_CONFIG.DEFAULT_ZOOM,
    });
    leafletMapRef.current = map;

    L.tileLayer(TILE_LAYER_CONFIG.URL, {
      attribution: TILE_LAYER_CONFIG.ATTRIBUTION,
    }).addTo(map);

    const onClick = (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng;
      setPicked({ lat, lng });

      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
      } else {
        markerRef.current = L.marker([lat, lng]).addTo(map);
      }
    };

    map.on("click", onClick);

    if (initialPosition) {
      markerRef.current = L.marker([
        initialPosition.lat,
        initialPosition.lng,
      ]).addTo(map);
    }

    return () => {
      map.off("click", onClick);
      map.remove();
      markerRef.current = null;
      leafletMapRef.current = null;
    };
  }, [center.lat, center.lng, initialPosition]);

  const handleConfirm = useCallback(async () => {
    if (!picked) return;

    try {
      setIsReverseGeocoding(true);

      const reverseUrl = new URL(
        NOMINATIM_CONFIG.REVERSE_ENDPOINT,
        NOMINATIM_CONFIG.BASE_URL
      );
      reverseUrl.searchParams.set("format", NOMINATIM_CONFIG.FORMAT);
      reverseUrl.searchParams.set("lat", picked.lat.toString());
      reverseUrl.searchParams.set("lon", picked.lng.toString());
      reverseUrl.searchParams.set(
        "addressdetails",
        NOMINATIM_CONFIG.ADDRESS_DETAILS.toString()
      );

      const response = await fetch(reverseUrl.toString());

      if (response.ok) {
        const data: NominatimReverseResult = await response.json();
        const address =
          data?.display_name ||
          `${picked.lat.toFixed(6)}, ${picked.lng.toFixed(6)}`;

        onSelect({ lat: picked.lat, lng: picked.lng, address });
      } else {
        onSelect({
          lat: picked.lat,
          lng: picked.lng,
          address: `${picked.lat.toFixed(6)}, ${picked.lng.toFixed(6)}`,
        });
      }
    } catch (error) {
      logErrorDev(ERROR_MESSAGES.MAP.REVERSE_GEOCODING_FAILED, error);
      onSelect({
        lat: picked.lat,
        lng: picked.lng,
        address: `${picked.lat.toFixed(6)}, ${picked.lng.toFixed(6)}`,
      });
    } finally {
      setIsReverseGeocoding(false);
    }
  }, [picked, onSelect]);

  return {
    mapRef,
    picked,
    isReverseGeocoding,
    searchQuery,
    setSearchQuery,
    isSearching,
    handleSearch,
    handleKeyPress,
    handleConfirm,
  };
};
